import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';

const LIST = 'suppliers';

// Supplier contacts (QM, QE, Facility Manager) are stored inline as JSON
// under ContactsJson. Per-plant billing assignments (added 2026-04) are
// stored under SupplierPlantsJson — shape is:
//   {
//     plantCode,
//     startDate, endDate,       // optional YYYY-MM-DD coverage window (see
//                               // coverageEndedOn / assignmentCoversDate)
//     service,                  // e.g. 'Standard Coverage' (default) or
//                               // 'Lineside Support' — (plantCode, service)
//                               // is the unique key; see findAssignment()
//     hourlyRate, hoursAllocated, hoursUnit, hoursUnitOther,
//     coverageStatus,
//     billSaturdays, billSundays, // weekend billing (Nov 2026); missing = not billed
//     hourlyRateHistory: [
//       { effectiveDate: 'YYYY-MM-DD', hourlyRate: Number }, ...
//     ],
//     coverageHistory: [        // dated coverage changes (Oct 2026)
//       { effectiveDate: 'YYYY-MM-DD', hoursAllocated: Number, hoursUnit: '' }, ...
//     ],
//     po: {                     // per-plant purchase order (added 2026-07)
//       type: 'Blanket PO' | 'Monthly PO',
//       number: '',
//       amount: Number | null,
//       initiationDate: 'YYYY-MM-DD' | null,   // Blanket only
//       expirationDate: 'YYYY-MM-DD' | null,   // Blanket only
//     } | null,
//   }
//
// `hourlyRate` is the current rate (kept as a top-level field for quick
// display and for legacy readers that don't know about history).
// `hourlyRateHistory` is the full timeline so Billing can price each
// timesheet day at the rate that was actually in effect.
//
// POs moved from supplier-level flat columns (PoType / BlanketPo* /
// MonthlyPo*) to per-plant `po` objects in 2026-07 so a supplier can hold
// a different PO at each plant. The legacy flat columns are still decoded:
// when a supplier has no per-plant POs yet, the legacy PO is carried onto
// the first plant assignment on read (see decode) and persists per-plant
// on the next save. `PoRequired` stays a supplier-level column. The
// assignment's `currentPO` free-text field is now derived from po.number
// so Invoice Draft auto-fill and other legacy readers keep working.
function decode(row) {
  if (!row) return row;
  let contacts = [];
  if (row.ContactsJson) {
    try {
      contacts = JSON.parse(row.ContactsJson);
    } catch {
      contacts = [];
    }
  }
  let plantAssignments = [];
  if (row.SupplierPlantsJson) {
    try {
      plantAssignments = JSON.parse(row.SupplierPlantsJson);
    } catch {
      plantAssignments = [];
    }
  }
  // Normalise rate history on every read so UI code can trust the
  // shape regardless of when the row was originally written. Legacy
  // rows that only have `hourlyRate` get a single synthetic history
  // entry seeded at the epoch-ish date below — a rate with no known
  // effective date is "whatever it was, has always been that."
  plantAssignments = plantAssignments.map((pa) => {
    const normalised = normaliseService(
      normalisePlantPo(normaliseCoverageHistory(normaliseRateHistory(pa))),
    );
    // The "In Review" coverage status was renamed to "Proposal Out".
    // Translate on read so legacy records still render under the new
    // palette and group on the Sales dashboard correctly. They get
    // permanently rewritten the next time the supplier is saved.
    if (normalised.coverageStatus === 'In Review') {
      return applyEndedStatus({ ...normalised, coverageStatus: 'Proposal Out' });
    }
    // Past its End Date → "Closed" (see applyEndedStatus). Also applied
    // on write, so the stored record catches up the next time it's saved.
    return applyEndedStatus(normalised);
  });
  // Legacy carry: suppliers saved before per-plant POs have a single
  // supplier-level PO in flat columns. Surface it as the first plant's
  // `po` so the form/detail/tracker only ever deal with per-plant POs.
  // Guarded so it never overwrites a real per-plant PO, and skipped once
  // any assignment carries one (i.e. after the first re-save).
  const hasPerPlantPo = plantAssignments.some((pa) => pa?.po?.type);
  if (
    !hasPerPlantPo &&
    plantAssignments.length > 0 &&
    row.PoRequired === 'Yes' &&
    (row.PoType === 'Blanket PO' || row.PoType === 'Monthly PO')
  ) {
    const isBlanket = row.PoType === 'Blanket PO';
    plantAssignments[0] = normalisePlantPo({
      ...plantAssignments[0],
      po: {
        type: row.PoType,
        number: isBlanket ? row.BlanketPoNumber || '' : row.MonthlyPoNumber || '',
        amount: isBlanket ? row.BlanketPoAmount ?? null : row.MonthlyPoAmount ?? null,
        initiationDate: isBlanket ? row.BlanketPoInitiationDate || null : null,
        expirationDate: isBlanket ? row.BlanketPoExpirationDate || null : null,
      },
    });
  }
  return { ...row, contacts, plantAssignments };
}

function encode(model) {
  const { contacts = [], plantAssignments = [], ...rest } = model;
  // Re-normalise on write so the stored history always has a sensible
  // shape even if the UI handed us something partial.
  const assignments = plantAssignments.map((pa) =>
    applyEndedStatus(
      normaliseService(normalisePlantPo(normaliseCoverageHistory(normaliseRateHistory(pa)))),
    ),
  );
  return {
    ...rest,
    ContactsJson: JSON.stringify(contacts),
    SupplierPlantsJson: JSON.stringify(assignments),
  };
}

// ---- Coverage window (startDate / endDate) — added 2026-09 -------------
//
// Each plant assignment may carry an optional `startDate` and `endDate`
// (YYYY-MM-DD). The end date marks the last day the supplier is covered
// at that plant. Once it has passed: the tracker stops seeding / offering
// the supplier there, days after it autofill to 0 and are flagged, billing
// finds no rate for those days (so they surface as "no rate on file"
// rather than pricing silently), and the missing-plant invoice alert skips
// the plant. Once the end date has passed, a live coverage status (Active,
// On Call, Paused, Suspended) is switched to "Closed" — on read, so every
// page agrees, and on save, so it sticks — and the UI still shows an
// "Ended" badge next to it (per Jillian, Sep 28 2026).

const isoOnly = (v) => String(v || '').slice(0, 10);

/** True when `date` (YYYY-MM-DD or Date) is after the assignment's end date. */
export function coverageEndedOn(pa, date) {
  const end = isoOnly(pa?.endDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return false;
  const d = isoDate(date);
  return Boolean(d) && d > end;
}

/** True when `date` is before the assignment's start date (when one is set). */
export function coverageNotStartedOn(pa, date) {
  const start = isoOnly(pa?.startDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return false;
  const d = isoDate(date);
  return Boolean(d) && d < start;
}

/** True when the assignment's window (if any) includes `date`. */
export function assignmentCoversDate(pa, date) {
  return !coverageEndedOn(pa, date) && !coverageNotStartedOn(pa, date);
}

/** True when the assignment has an end date that is already in the past. */
export function assignmentHasEnded(pa, today = new Date()) {
  return coverageEndedOn(pa, today);
}

// Statuses that mean "we are (or could be) covering this plant". Anything
// else (Prospecting, Proposal Out, Declined, Closed) is left alone when the
// end date passes.
const LIVE_COVERAGE_STATUSES = new Set(['Active', 'On Call', 'Paused', 'Suspended']);
export const ENDED_COVERAGE_STATUS = 'Closed';

/**
 * Return the assignment with its coverage status set to "Closed" when its
 * end date has passed and it still carries a live status. Otherwise the
 * assignment is returned unchanged (same object).
 */
export function applyEndedStatus(pa, today = new Date()) {
  if (!pa || !assignmentHasEnded(pa, today)) return pa;
  const status = pa.coverageStatus || 'Active';
  if (!LIVE_COVERAGE_STATUSES.has(status)) return pa;
  return { ...pa, coverageStatus: ENDED_COVERAGE_STATUS };
}

/**
 * "8 Hours / Week" for the supplier + plant (+ service) on a tracker
 * row, from the Coverage Hours and Unit saved on the Supplier page;
 * shared by the Billing Tracker and the Approvals view so both say
 * exactly the same thing;
 * "On Call" when that assignment's Coverage Status is On Call.
 * Empty string when there's nothing to show (no supplier picked, no
 * assignment at the plant, or no hours on file).
 */
export function coverageCaption(row, suppliers, dates) {
  if (!row?.plantCode || !row?.supplierCode) return '';
  const supplier = (suppliers || []).find(
    (s) => (s.SupplierCode || '') === row.supplierCode,
  );
  if (!supplier) return '';
  const pa = findAssignment(supplier, row.plantCode, row.service);
  if (!pa) return '';
  // On Call suppliers have no standing hours to measure against, so the
  // caption says so instead of showing a number (per Jillian, Oct 2026).
  if ((pa.coverageStatus || '') === 'On Call') return 'On Call';
  // With the week's dates, list each coverage amount in effect that week
  // and the date it starts (Oct 2026): "4 Hours / Day, 6 Hours / Day from 10/14".
  if (Array.isArray(dates) && dates.length) {
    const parts = [];
    let prev = null;
    dates.forEach((d, i) => {
      const label = formatCoverage(coverageOn(pa, d));
      if (label === prev) return;
      prev = label;
      if (!label) return;
      if (i === 0 || parts.length === 0) {
        parts.push(i === 0 ? label : `${label} from ${shortDate(d)}`);
      } else {
        parts.push(`${label} from ${shortDate(d)}`);
      }
    });
    return parts.join(', ');
  }
  return formatCoverage(coverageOn(pa, new Date()));
}

function shortDate(d) {
  const iso = isoDate(d);
  return iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : '';
}

// ---- Per-plant Service plumbing (added 2026-09) ----------------------
//
// A supplier can now hold MORE THAN ONE row for the same plant, each a
// different "service" billed at its own rate and hours — e.g. Nascote at
// BGA: "Standard Coverage" (daily rep support, $35/hr) and "Lineside
// Support" (18 hrs/day, $30/hr). The pair (plantCode, service) is the
// unique key for an assignment; plantCode alone no longer is.
//
// Every assignment carries `service`. Rows saved before this existed get
// DEFAULT_SERVICE on read (and are rewritten with it on the next save), so
// every existing supplier keeps working with no data migration.
//
// Callers that used to look up an assignment by plant code should go
// through findAssignment() below, passing the service when they have one
// (timesheet rows carry it). With no service, a plant that has exactly one
// row resolves to that row; a plant with several resolves to its
// "Standard Coverage" row, so legacy trackers keep billing the way they
// always did.

export const DEFAULT_SERVICE = 'Standard Coverage';

// Dropdown choices for the Service field on the Supplier form (per
// Jillian, Sep 2026). Add to this list when a new kind of billable
// service comes along; the default stays first.
export const SERVICE_OPTIONS = [DEFAULT_SERVICE, 'Lineside Support'];

export function serviceKey(service) {
  return String(service || '').trim().toLowerCase();
}

function normaliseService(pa) {
  if (!pa || typeof pa !== 'object') return pa;
  const service = String(pa.service || '').trim() || DEFAULT_SERVICE;
  return { ...pa, service };
}

/**
 * Resolve ONE plant assignment for a supplier.
 *
 * @param {Object|Array} supplierOrAssignments  decoded supplier, or its
 *                                              plantAssignments array
 * @param {string} plantCode
 * @param {string} [service]  the row's service name, when known
 * @returns {Object|null}
 */
export function findAssignment(supplierOrAssignments, plantCode, service) {
  const assignments = Array.isArray(supplierOrAssignments)
    ? supplierOrAssignments
    : supplierOrAssignments?.plantAssignments || [];
  const target = String(plantCode || '').toUpperCase().trim();
  if (!target) return null;
  const atPlant = assignments.filter(
    (pa) => String(pa?.plantCode || '').toUpperCase().trim() === target,
  );
  if (atPlant.length === 0) return null;
  if (atPlant.length === 1) return atPlant[0];
  const want = serviceKey(service);
  if (want) {
    const exact = atPlant.find((pa) => serviceKey(pa.service) === want);
    if (exact) return exact;
  }
  // No usable service given (legacy tracker row, or a caller that only
  // knows the plant): prefer the default row, else the first one listed.
  return (
    atPlant.find((pa) => serviceKey(pa.service) === serviceKey(DEFAULT_SERVICE)) ||
    atPlant[0]
  );
}

/**
 * Every assignment a supplier has at a plant (one per service), in the
 * order they appear on the supplier record. Used by the tracker to decide
 * whether to show a Service dropdown for a row.
 */
export function assignmentsAtPlant(supplierOrAssignments, plantCode) {
  const assignments = Array.isArray(supplierOrAssignments)
    ? supplierOrAssignments
    : supplierOrAssignments?.plantAssignments || [];
  const target = String(plantCode || '').toUpperCase().trim();
  if (!target) return [];
  return assignments.filter(
    (pa) => String(pa?.plantCode || '').toUpperCase().trim() === target,
  );
}

/**
 * Display label for an assignment's service. Hides the default name so
 * single-service suppliers read exactly as before; a non-default service
 * shows its name. Pass `always` to show the default too (e.g. on the
 * supplier's own detail page where every row is listed).
 */
export function serviceLabel(pa, { always = false } = {}) {
  const service = String(pa?.service || '').trim() || DEFAULT_SERVICE;
  if (!always && serviceKey(service) === serviceKey(DEFAULT_SERVICE)) return '';
  return service;
}

// ---- Per-plant PO plumbing -----------------------------------------

/**
 * Return the plant-assignment shape with a well-formed `po` — either null
 * or { type, number, amount, initiationDate, expirationDate } — and with
 * the legacy `currentPO` free-text field mirrored from po.number so
 * Invoice Draft auto-fill and older readers keep working. Idempotent —
 * safe to call on every decode and encode.
 */
// PO types (per Jillian, Aug 2026). Yearly + Quarterly carry a balance
// (start/expiration window + drawdown) — the old "Blanket PO" is now
// mapped to "Yearly PO". Monthly is a flat per-month PO with no balance.
// PO Pending marks a plant awaiting its PO number (temporary).
export const PO_TYPES = ['Yearly PO', 'Quarterly PO', 'Monthly PO', 'PO Pending'];
export const PO_BALANCE_TYPES = ['Yearly PO', 'Quarterly PO'];
export function poCarriesBalance(type) {
  return type === 'Yearly PO' || type === 'Quarterly PO';
}
// Legacy "Blanket PO" → "Yearly PO" on read.
function normalisePoType(type) {
  return type === 'Blanket PO' ? 'Yearly PO' : type;
}

function normalisePlantPo(pa) {
  if (!pa || typeof pa !== 'object') return pa;
  const raw = pa.po;
  const type = raw && typeof raw === 'object' ? normalisePoType(raw.type) : '';
  const validType = raw && typeof raw === 'object' && PO_TYPES.includes(type);
  if (!validType) {
    return { ...pa, po: null, currentPO: (pa.currentPO || '').trim() };
  }
  const carriesDates = poCarriesBalance(type);
  const amount = Number(raw.amount);
  const po = {
    type,
    number: String(raw.number || '').trim(),
    amount:
      raw.amount === '' || raw.amount == null || !Number.isFinite(amount)
        ? null
        : amount,
    initiationDate: carriesDates ? raw.initiationDate || null : null,
    expirationDate: carriesDates ? raw.expirationDate || null : null,
    notes: String(raw.notes || '').trim(),
  };
  return { ...pa, po, currentPO: po.number };
}

// ---- Rate history plumbing -----------------------------------------

// Default effective date for legacy plant assignments that have an
// hourlyRate but no history. Far enough in the past that any timesheet
// date will sort after it, so the lookup always resolves to a rate.
const RATE_HISTORY_EPOCH = '1970-01-01';

/**
 * Return the plant-assignment shape with a guaranteed `hourlyRateHistory`
 * array, sorted oldest → newest, and with the top-level `hourlyRate`
 * kept in sync with the most recent entry. Idempotent — safe to call on
 * every decode and encode.
 */
function normaliseRateHistory(pa) {
  if (!pa || typeof pa !== 'object') return pa;
  const fromHistory = Array.isArray(pa.hourlyRateHistory)
    ? [...pa.hourlyRateHistory]
    : [];
  // Drop empty / invalid entries, coerce dates to YYYY-MM-DD.
  const cleaned = fromHistory
    .map((h) => ({
      effectiveDate: String(h.effectiveDate || '').slice(0, 10),
      hourlyRate: Number(h.hourlyRate),
    }))
    .filter(
      (h) =>
        h.effectiveDate &&
        /^\d{4}-\d{2}-\d{2}$/.test(h.effectiveDate) &&
        Number.isFinite(h.hourlyRate),
    )
    .sort((a, b) =>
      a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0,
    );
  // If there's no history, seed one from the top-level hourlyRate so the
  // supplier doesn't lose its rate on first re-save. Uses the epoch
  // effective date so any lookup resolves.
  const topRate = Number(pa.hourlyRate);
  if (cleaned.length === 0 && Number.isFinite(topRate)) {
    cleaned.push({
      effectiveDate: RATE_HISTORY_EPOCH,
      hourlyRate: topRate,
    });
  }
  // One entry per effective date (the later one in the list wins).
  const deduped = dedupeByDate(cleaned);
  // Top-level hourlyRate mirrors the entry in effect TODAY (Oct 2026:
  // changes can be scheduled for a future date, so "latest" is no
  // longer the same as "current").
  const current = entryInEffect(deduped, todayIsoLocal());
  return {
    ...pa,
    hourlyRate: current ? current.hourlyRate : pa.hourlyRate,
    hourlyRateHistory: deduped,
  };
}

// ---- Coverage history (Oct 2026) ---------------------------------------
//
// Coverage Hours + Coverage Unit can change over time (4 Hours / Week, then
// 6 Hours / Week from 11/02). Each plant assignment keeps a dated
// `coverageHistory`, set from the Coverage History box on the supplier
// form. The top-level hoursAllocated / hoursUnit always mirror the entry
// in effect TODAY, so every page that shows "current coverage" keeps
// working unchanged; the Billing Tracker, Approvals and invoices look up
// the coverage in effect on each specific day with coverageOn().

function dedupeByDate(sorted) {
  const out = [];
  for (const h of sorted) {
    if (out.length && out[out.length - 1].effectiveDate === h.effectiveDate) {
      out[out.length - 1] = h;
    } else {
      out.push(h);
    }
  }
  return out;
}

/**
 * The entry in effect on `iso` (YYYY-MM-DD) from a history sorted oldest →
 * newest: the newest entry dated on or before it. When every entry is
 * later than `iso`, the earliest entry is used so a lookup always resolves.
 */
function entryInEffect(history, iso) {
  if (!Array.isArray(history) || history.length === 0) return null;
  // Doesn't assume sorted input, so raw form state works too.
  let best = null;
  let earliest = null;
  for (const h of history) {
    const d = String(h?.effectiveDate || '');
    if (!d) continue;
    if (!earliest || d < earliest.effectiveDate) earliest = h;
    if (d <= iso && (!best || d >= best.effectiveDate)) best = h;
  }
  return best || earliest;
}

function cleanHours(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Guarantee a sorted, de-duplicated `coverageHistory` on a plant assignment
 * and sync the top-level hoursAllocated / hoursUnit to today's entry.
 * Legacy rows with no history get one entry seeded from their current
 * values at the epoch date (meaning "this has always been the coverage").
 * Idempotent: safe on every decode and encode.
 */
function normaliseCoverageHistory(pa) {
  if (!pa || typeof pa !== 'object') return pa;
  const raw = Array.isArray(pa.coverageHistory) ? pa.coverageHistory : [];
  const cleaned = dedupeByDate(
    raw
      .map((h) => ({
        effectiveDate: String(h?.effectiveDate || '').slice(0, 10),
        hoursAllocated: cleanHours(h?.hoursAllocated),
        hoursUnit: String(h?.hoursUnit || '').trim(),
      }))
      .filter(
        (h) =>
          /^\d{4}-\d{2}-\d{2}$/.test(h.effectiveDate) &&
          (h.hoursAllocated != null || h.hoursUnit),
      )
      .sort((a, b) =>
        a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0,
      ),
  );
  if (cleaned.length === 0) {
    const hours = cleanHours(pa.hoursAllocated);
    const unit =
      pa.hoursUnit === 'Other'
        ? String(pa.hoursUnitOther || '').trim()
        : String(pa.hoursUnit || '').trim();
    if (hours != null || unit) {
      cleaned.push({ effectiveDate: RATE_HISTORY_EPOCH, hoursAllocated: hours, hoursUnit: unit });
    }
  }
  const current = entryInEffect(cleaned, todayIsoLocal());
  if (!current) return { ...pa, coverageHistory: cleaned };
  return {
    ...pa,
    coverageHistory: cleaned,
    hoursAllocated: current.hoursAllocated,
    hoursUnit: current.hoursUnit,
    hoursUnitOther: '',
  };
}

/**
 * Normalise both histories on a plant assignment and sync its top-level
 * hourlyRate / hoursAllocated / hoursUnit to the entries in effect today.
 * Used by the supplier form before save so the Coverage Activity diff
 * compares like with like.
 */
export function syncPlantHistories(pa) {
  return normaliseCoverageHistory(normaliseRateHistory(pa));
}

/** Today as YYYY-MM-DD (local). */
export function todayIso() {
  return todayIsoLocal();
}

/**
 * Coverage in effect for a plant assignment on `date` (Date or
 * YYYY-MM-DD): { hoursAllocated, hoursUnit }. Falls back to the
 * assignment's top-level values when it has no history. Null when
 * there's nothing on file.
 */
export function coverageOn(pa, date) {
  if (!pa) return null;
  const iso = isoDate(date) || todayIsoLocal();
  const hit = entryInEffect(pa.coverageHistory, iso);
  if (hit) return { hoursAllocated: hit.hoursAllocated, hoursUnit: hit.hoursUnit };
  const unit =
    pa.hoursUnit === 'Other' ? String(pa.hoursUnitOther || '').trim() : pa.hoursUnit || '';
  if (pa.hoursAllocated == null && !unit) return null;
  return { hoursAllocated: cleanHours(pa.hoursAllocated), hoursUnit: unit };
}

/** "8 Hours / Week" for one coverage entry ('' when there are no hours). */
export function formatCoverage(cov) {
  const h = Number(cov?.hoursAllocated);
  if (!Number.isFinite(h) || h <= 0) return '';
  const hours = Math.round(h * 100) / 100;
  let unit = String(cov?.hoursUnit || '').trim().replace(/^per\s+/i, '');
  if (!unit) return `${hours} Hours`;
  unit = unit.charAt(0).toUpperCase() + unit.slice(1);
  return `${hours} Hours / ${unit}`;
}

/**
 * Return the hourly rate that was in effect for a given plant assignment
 * on a specific date. Walks the assignment's rate history and picks the
 * entry with the latest `effectiveDate` that's still on or before the
 * target date. Returns null if the supplier / plant / date triple can't
 * be resolved — callers should treat null as "no billable rate found"
 * and skip (or flag for review) rather than defaulting to zero.
 *
 * `date` can be a Date, a YYYY-MM-DD string, or anything Date can parse.
 */
export function getRateFor(supplier, plantCode, date, service) {
  if (!supplier || !plantCode || !date) return null;
  // `service` picks between several rows at the same plant (see
  // findAssignment). Omitted → the plant's only / default row, so every
  // existing caller keeps its old behaviour.
  const assignment = findAssignment(supplier, plantCode, service);
  if (!assignment) return null;
  const iso = isoDate(date);
  if (!iso) return null;
  // No rate after the coverage end date — hours logged there surface as
  // "no rate on file" instead of being priced silently (Sep 2026).
  if (coverageEndedOn(assignment, iso)) return null;
  const history = assignment.hourlyRateHistory || [];
  // History is sorted oldest → newest via normaliseRateHistory. Walk
  // from the newest side and pick the first entry whose effectiveDate
  // is ≤ iso.
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const h = history[i];
    if (h.effectiveDate <= iso) return h.hourlyRate;
  }
  // Date is earlier than any known rate. Fall back to the top-level
  // current rate if we have one (better than returning null for a
  // legacy supplier with no history at all — decode normalisation
  // should've prevented this, but defensive is cheap).
  return Number.isFinite(Number(assignment.hourlyRate))
    ? Number(assignment.hourlyRate)
    : null;
}

// Coerce any date-ish input to a YYYY-MM-DD string. Returns '' on
// failure so callers can short-circuit.
function isoDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ---- Proposal → coverage plumbing ----------------------------------
//
// These pure helpers translate an outgoing proposal into the supplier's
// per-plant coverage model. They DON'T touch SharePoint or log anything —
// callers persist with createSupplier/updateSupplier and log the returned
// `event` via logSupplierPlantAssignmentEvent. Keeping them pure makes the
// win/lose math easy to reason about (and test) in isolation.

// The coverage status a plant assignment carries while a proposal is
// outstanding. Matches SupplierForm's COVERAGE_STATUSES and the legacy
// "In Review" → "Proposal Out" rename handled in decode().
export const PROPOSAL_OUT_STATUS = 'Proposal Out';

// Locate an assignment for a plant code, case-insensitively. Returns the
// index or -1.
function findAssignmentIndex(assignments, plantCode) {
  const target = String(plantCode || '').toUpperCase().trim();
  return (assignments || []).findIndex(
    (pa) => String(pa.plantCode || '').toUpperCase().trim() === target,
  );
}

function todayIsoLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/**
 * Add or update a "Proposal Out" plant assignment on a supplier from one
 * proposal coverage line.
 *
 * KEY RULE (decision #7): the proposed hourly rate is parked in `proposedRate`
 * and deliberately kept OUT of `hourlyRate` / `hourlyRateHistory`. Billing's
 * getRateFor() therefore returns null for the plant and can't price it until
 * the deal is won via winProposalAssignment(), which promotes the proposed
 * rate into the real rate history. Re-proposing a plant that's already Active
 * leaves its live rate untouched.
 *
 * @param {Object} supplier  decoded supplier (with plantAssignments)
 * @param {Object} line      { plantCode, proposedRate, hoursAllocated, hoursUnit,
 *                             startDate?, notes? }
 * @returns {{ plantAssignments: Array, event: Object }}
 */
export function withProposalAssignment(supplier, line) {
  const assignments = Array.isArray(supplier?.plantAssignments)
    ? supplier.plantAssignments.map((a) => ({ ...a }))
    : [];
  const plantCode = String(line.plantCode || '').toUpperCase().trim();
  const proposedRate =
    line.proposedRate === '' || line.proposedRate == null
      ? null
      : Number(line.proposedRate);
  const hoursAllocated =
    line.hoursAllocated === '' || line.hoursAllocated == null
      ? null
      : Number(line.hoursAllocated);

  const idx = findAssignmentIndex(assignments, plantCode);
  const existing = idx >= 0 ? assignments[idx] : null;

  // Safety: never downgrade a live (Active) plant back to Proposal Out — that
  // would silently stop its billing. Re-sending a proposal for an already
  // Active plant is a no-op on the coverage record (the proposal PDF/record
  // still saves; the supplier's live coverage is left untouched).
  if (existing && existing.coverageStatus === 'Active') {
    return { plantAssignments: assignments, event: null };
  }

  const next = {
    plantCode,
    // Preserve the plant's PO (and its mirrored currentPO) — a re-sent
    // proposal must never wipe billing's PO record for the plant.
    po: existing?.po ?? null,
    currentPO: existing?.currentPO ?? '',
    // Proposed rate is staged in `proposedRate` (below); the real rate stays
    // null so Billing can't price the plant until the deal is won. (Active
    // plants never reach here — see the early return above.)
    hourlyRate: null,
    hourlyRateHistory: existing?.hourlyRateHistory ?? [],
    proposedRate,
    hoursAllocated,
    hoursUnit: line.hoursUnit ?? existing?.hoursUnit ?? '',
    coverageStatus: PROPOSAL_OUT_STATUS,
    startDate: line.startDate ?? existing?.startDate ?? '',
    notes: existing?.notes ?? line.notes ?? '',
  };

  const event = {
    supplierCode: supplier?.SupplierCode || '',
    supplierName: supplier?.Title || '',
    plantCode,
    eventType: existing ? 'Revised' : 'Added',
    previousStatus: existing?.coverageStatus || '',
    newStatus: PROPOSAL_OUT_STATUS,
    previousHours: existing ? Number(existing.hoursAllocated) || 0 : 0,
    newHours: hoursAllocated || 0,
    hoursUnit: next.hoursUnit,
    summary: `Proposal out for ${plantCode}`,
  };

  if (idx >= 0) assignments[idx] = next;
  else assignments.push(next);
  return { plantAssignments: assignments, event };
}

/**
 * Win a proposal: flip the plant assignment to Active and promote the staged
 * `proposedRate` into the real rate (seeding rate history at the effective
 * date). This is the moment the hours start counting in the Sales Tracker and
 * the rate becomes billable.
 *
 * @param {Object} supplier   decoded supplier
 * @param {string} plantCode
 * @param {Object} [opts]     { effectiveDate?: 'YYYY-MM-DD' }
 * @returns {{ plantAssignments: Array, event: Object|null }}
 */
export function winProposalAssignment(supplier, plantCode, opts = {}) {
  const assignments = Array.isArray(supplier?.plantAssignments)
    ? supplier.plantAssignments.map((a) => ({ ...a }))
    : [];
  const idx = findAssignmentIndex(assignments, plantCode);
  if (idx < 0) return { plantAssignments: assignments, event: null };
  const pa = assignments[idx];
  const effectiveDate =
    opts.effectiveDate || (pa.startDate || '').slice(0, 10) || todayIsoLocal();
  // Promote the staged proposed rate (fall back to any existing rate).
  const rate =
    pa.proposedRate === '' || pa.proposedRate == null
      ? Number(pa.hourlyRate)
      : Number(pa.proposedRate);
  const hasRate = Number.isFinite(rate);
  const history = Array.isArray(pa.hourlyRateHistory)
    ? [...pa.hourlyRateHistory]
    : [];
  if (hasRate) {
    history.push({ effectiveDate, hourlyRate: rate });
  }
  const next = {
    ...pa,
    coverageStatus: 'Active',
    hourlyRate: hasRate ? rate : pa.hourlyRate,
    hourlyRateHistory: history,
    startDate: pa.startDate || effectiveDate,
  };
  delete next.proposedRate;

  const event = {
    supplierCode: supplier?.SupplierCode || '',
    supplierName: supplier?.Title || '',
    plantCode: next.plantCode,
    eventType: 'Revised',
    previousStatus: pa.coverageStatus || '',
    newStatus: 'Active',
    previousHours: Number(pa.hoursAllocated) || 0,
    newHours: Number(pa.hoursAllocated) || 0,
    hoursUnit: next.hoursUnit || '',
    summary: `Proposal won — ${next.plantCode} now Active`,
  };

  assignments[idx] = next;
  return { plantAssignments: assignments, event };
}

/**
 * Lose a proposal: flip the plant assignment to Closed. The staged proposed
 * rate is dropped and no rate history is written, so nothing ever became
 * billable. History (the row itself) is preserved for the audit trail.
 *
 * @returns {{ plantAssignments: Array, event: Object|null }}
 */
export function loseProposalAssignment(supplier, plantCode) {
  const assignments = Array.isArray(supplier?.plantAssignments)
    ? supplier.plantAssignments.map((a) => ({ ...a }))
    : [];
  const idx = findAssignmentIndex(assignments, plantCode);
  if (idx < 0) return { plantAssignments: assignments, event: null };
  const pa = assignments[idx];
  const next = { ...pa, coverageStatus: 'Closed' };
  delete next.proposedRate;

  const event = {
    supplierCode: supplier?.SupplierCode || '',
    supplierName: supplier?.Title || '',
    plantCode: next.plantCode,
    eventType: 'Revised',
    previousStatus: pa.coverageStatus || '',
    newStatus: 'Closed',
    previousHours: Number(pa.hoursAllocated) || 0,
    newHours: Number(pa.hoursAllocated) || 0,
    hoursUnit: next.hoursUnit || '',
    summary: `Proposal lost — ${next.plantCode} closed`,
  };

  assignments[idx] = next;
  return { plantAssignments: assignments, event };
}

export async function listSuppliers(opts) {
  const rows = await listItems(LIST, { orderby: 'fields/Title', ...opts });
  return rows.map(decode);
}

export async function getSupplier(id) {
  return decode(await getItem(LIST, id));
}

/**
 * Look up a supplier by its short code (e.g. 'AUTOSYS'). Used by plant detail
 * pages to resolve SupplierCodesJson entries into full records.
 */
export async function getSupplierByCode(supplierCode) {
  const rows = await listItems(LIST, {
    filter: `fields/SupplierCode eq '${supplierCode}'`,
    top: 1,
  });
  return rows.length ? decode(rows[0]) : null;
}

/**
 * Fetch multiple suppliers by code in one pass. Returns a map { code: supplier }.
 * Graph filter syntax for "in" is a chain of `or`s, but for the small counts
 * we expect per plant (~3-5 suppliers) it's fine to parallelize single fetches.
 */
export async function getSuppliersByCodes(codes) {
  const unique = [...new Set(codes.filter(Boolean))];
  const entries = await Promise.all(
    unique.map(async (code) => [code, await getSupplierByCode(code)]),
  );
  return Object.fromEntries(entries.filter(([, s]) => s));
}

/**
 * Return every supplier that has a direct plant assignment for the given
 * plant code (i.e. an entry in SupplierPlantsJson whose plantCode matches).
 * These assignments come from the Suppliers form's "Plant Locations &
 * Coverage" table and represent on-site billing arrangements that exist
 * independently of any parts. The Plant detail page merges this list with
 * the parts-derived supplier list so a supplier shows up on its plant even
 * when no parts have been linked yet.
 *
 * Graph can't filter inside a JSON note column, so this fetches all
 * suppliers and filters client-side. Fine for the handful of suppliers
 * Proxemics has today.
 */
export async function listSuppliersForPlant(plantCode) {
  if (!plantCode) return [];
  const target = String(plantCode).toUpperCase();
  const all = await listSuppliers();
  return all.filter((s) =>
    (s.plantAssignments || []).some(
      (pa) => String(pa.plantCode || '').toUpperCase() === target,
    ),
  );
}

export async function createSupplier(model) {
  return decode(await createItem(LIST, encode(model)));
}

export async function updateSupplier(id, model) {
  return decode(await updateItem(LIST, id, encode(model)));
}

export async function deleteSupplier(id) {
  return deleteItem(LIST, id);
}
